<script lang="ts" setup>
import type { OrdenationDropdownOption } from '../Filters/OrdenationSelect';
import type { CtaClickedPayload, CuratedListPropertyListProps } from './';
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';

import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';

import { OrdenationSelect } from '../Filters/OrdenationSelect';

const props = defineProps<CuratedListPropertyListProps>();

const isRunningOnServer = import.meta.server;

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
	return [
		{ label: 'Início', route: '/' },
		{
			label: `Coleção ${props.curatedList?.creator_details.name}`,
			route: `/colecao/${props.curatedList?.slug}`
		}
	];
});

const { trackBreadcrumbClicked, trackReorderClicked, trackCtaViewMoreClicked } =
	useEvents();

function handleBreadcrumbClicked(breadcrumbItem: BreadcrumbItemWithIndex) {
	trackBreadcrumbClicked({
		[MixpanelProps.CURRENT_PAGE]: `Coleção ${props.curatedList?.name}`,
		[MixpanelProps.CLICK_HOME]: breadcrumbItem.index === 0,
		[MixpanelProps.CTA_TEXT]: breadcrumbItem.label,
		[MixpanelProps.DESTINATION_URL]:
			(breadcrumbItem.route as Record<string, string>).path || '/'
	});
}

function handleReorderClicked(value: OrdenationDropdownOption['value']) {
	trackReorderClicked({
		[MixpanelProps.CLICK_EXCLUSIVES]: value === 'pilar_exclusivities',
		[MixpanelProps.CLICK_HIGHLIGHTS]: value === 'highlights',
		[MixpanelProps.CLICK_LOWEST_PRICE]: value === 'asking_price_asc',
		[MixpanelProps.CLICK_HIGHEST_PRICE]: value === 'asking_price_desc',
		[MixpanelProps.CLICK_FEWER_PHOTOS]: value === 'images_count_asc',
		[MixpanelProps.CLICK_MORE_PHOTOS]: value === 'images_count_desc',
		[MixpanelProps.CLICK_MORE_RELEVANT]: value === 'default_ordenation',
		[MixpanelProps.CLICK_MORE_RECENT]: value === 'wishlist_recent',
		[MixpanelProps.CLICK_OLDEST]: value === 'wishlist_oldest'
	});
}

function handleDiscoverPropertiesCardClicked(payload: CtaClickedPayload) {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: `Coleção ${props.curatedList?.name}`,
		[MixpanelProps.SECTION_NAME]: 'card view more collection',
		[MixpanelProps.CTA_TEXT]: payload.cta_text,
		[MixpanelProps.DESTINATION_URL]: payload.destination_url
	});
}

function handleViewMoreClicked() {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: `Coleção ${props.curatedList?.name}`,
		[MixpanelProps.SECTION_NAME]: 'card view more collection',
		[MixpanelProps.CTA_TEXT]: 'Ver mais',
		[MixpanelProps.DESTINATION_URL]: `/colecao/${props.curatedList?.slug}`
	});

	props.nextPage();
}
</script>

<template>
	<div class="flex flex-col divide-y divide-white-dark-2 bg-white">
		<div class="flex flex-col lg:gap-12">
			<Container class="bg-white px-5 py-3 md:py-3 lg:pb-0 lg:pt-5">
				<Breadcrumb
					v-if="breadcrumbItems.length"
					:items="breadcrumbItems"
					@breadcrumb-clicked="handleBreadcrumbClicked"
				/>
			</Container>
			<Container
				class="flex flex-col gap-y-5 bg-white pt-0 md:pt-0"
				main-class="flex flex-col gap-6"
			>
				<div class="flex w-full items-end justify-between pt-3 lg:pt-0">
					<div
						v-if="isFetchingForTheFirstTime && !isRunningOnServer"
						class="w-[70%] space-y-1"
					>
						<Skeleton is-active variant="white" class="h-[28px] w-[100px]" />
						<Skeleton is-active variant="white" class="h-[21px] w-[200px]" />
					</div>

					<div
						v-else-if="hasProperties"
						class="flex w-full items-center justify-between"
					>
						<div class="flex max-w-[70%] text-left">
							<h1
								class="flex flex-col font-matter-sq text-xl font-normal leading-normal tracking-[-0.4px] text-primary"
							>
								<span>
									{{ propertyCountLabel }}
								</span>
							</h1>
						</div>
						<OrdenationSelect
							:sort-by="filters.sortBy"
							:order-by="filters.orderBy"
							class="self-center"
							@update-selection="updateFiltersManager"
							@reorder-clicked="handleReorderClicked"
						/>
					</div>
				</div>
				<div>
					<div class="property-card-grid-collection pb-5">
						<div
							v-show="
								(!isFetchingForTheFirstTime || isRunningOnServer) &&
								hasProperties
							"
							class="col-span-full row-3 h-auto min-h-[84px] w-full md:col-[span_1_/-1] md:row-1 md:h-[378px]"
						>
							<DiscoverPropertiesCardDiscoverCard
								class="size-full min-h-[279px] sm:w-[calc(100%-16px)] md:max-w-fit"
								card-class="h-[245px]"
								@cta_clicked="handleDiscoverPropertiesCardClicked"
							/>
						</div>

						<template v-if="isFetchingForTheFirstTime">
							<PropertyCardSkeleton
								v-for="index in 10"
								:key="`initial-skeleton-${index}`"
								variant="whiteBackground"
							/>
						</template>

						<template v-else>
							<div
								v-for="(property, index) in properties"
								:key="property.id"
								class="h-[340px] md:h-[378px]"
							>
								<PropertyCard
									variant="whiteBackground"
									size="md"
									class="cursor-pointer"
									:property="property"
									section="collection properties"
									:card-position="index"
									:tracking-context="trackingContext"
									:position-in-list="index"
								/>
							</div>

							<div
								v-if="isFetching"
								class="contents contain-[layout] [overflow-anchor:none]"
							>
								<div
									v-for="index in 9"
									:key="`pagination-skeleton-${index}`"
									class="h-[340px] md:h-[378px] [content-visibility:auto] contain-[style_layout] [overflow-anchor:none]"
								>
									<PropertyCardSkeleton variant="whiteBackground" />
								</div>
							</div>
						</template>
					</div>

					<div class="contain-[style_layout] [overflow-anchor:none]">
						<Button
							v-if="shouldShowNextPageButton"
							class="mx-auto mt-5 w-[150px] cursor-pointer px-11 font-medium"
							@click="handleViewMoreClicked"
						>
							<IconPilarCircularProgress
								v-if="isFetching"
								class="h-[26px] animate-spin text-white"
							/>
							<p v-else>Ver mais</p>
						</Button>
					</div>
				</div>
			</Container>
		</div>
	</div>
</template>
